/**
 * /frontend/src/features/analytics/components/shared/ChartContainer.js
 */

import React from 'react';
import EmptyState from './EmptyState';
import ExportCSVButton from './ExportCSVButton';
import LoadingSkeleton from './LoadingSkeleton';
import { useAnalyticsViewContext } from './AnalyticsViewContext';
import './ChartContainer.css';

/**
 * Standard analytics chart card wrapper.
 * @param {{title:string,subtitle?:string,helpText?:string,children:any,exportFilename?:string,exportRows?:any[],isLoading?:boolean,isEmpty?:boolean,emptyMessage?:string,actions?:React.ReactNode}} props
 * @returns {JSX.Element}
 */
const ChartContainer = ({
  title,
  subtitle,
  helpText,
  children,
  exportFilename,
  exportRows,
  isLoading = false,
  isEmpty = false,
  emptyMessage = 'No analytics data available for the selected period.',
  actions = null
}) => {
  const { appliedStartDate, appliedEndDate } = useAnalyticsViewContext();
  const dateRangeLabel = formatDateRange(appliedStartDate, appliedEndDate);
  const resolvedExportRows = exportRows && exportRows.length
    ? exportRows
    : inferRowsFromChildren(children);

  return (
    <article className="analytics-chart-card">
      <header className="analytics-chart-header">
        <div className="analytics-chart-title-wrap">
          <h4 className="analytics-chart-title">{title}</h4>
          <span className="analytics-chart-help" title={helpText || subtitle || 'Metric details for this chart'}>
            ⓘ
          </span>
        </div>
        <div className="analytics-chart-actions">
          {actions}
          <ExportCSVButton
            filename={exportFilename || slugifyFilename(title)}
            rows={resolvedExportRows}
            title={title}
            className="btn ghost sm analytics-chart-export-btn"
          />
        </div>
      </header>

      {subtitle || dateRangeLabel ? (
        <p className="analytics-chart-subtitle">
          {subtitle}
          {subtitle && dateRangeLabel ? ' · ' : ''}
          {dateRangeLabel}
        </p>
      ) : null}

      <div className="analytics-chart-body">
        {isLoading ? <LoadingSkeleton /> : null}
        {!isLoading && isEmpty ? <EmptyState message={emptyMessage} /> : null}
        {!isLoading && !isEmpty ? children : null}
      </div>
    </article>
  );
};

const formatDateRange = (startDate, endDate) => {
  if (!isIsoDate(startDate) || !isIsoDate(endDate)) {
    return '';
  }
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return '';
  }
  const formatter = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  return `${formatter.format(start)} - ${formatter.format(end)}`;
};

const isIsoDate = (value) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);

const slugifyFilename = (label) => `analytics-${String(label || 'chart')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/(^-|-$)/g, '')}.csv`;

const inferRowsFromChildren = (children) => {
  const nodes = React.Children.toArray(children);
  for (const node of nodes) {
    if (!React.isValidElement(node) || !node.props) {
      continue;
    }
    const candidate = node.props.data || node.props.rows || node.props.items;
    if (Array.isArray(candidate) && candidate.length > 0 && typeof candidate[0] === 'object') {
      return candidate;
    }
  }
  return [];
};

export default ChartContainer;
